<template>
	<div class="display-flex flex-column width-100">
		<WidgetFormLogo :widgetMetadata="widgetMetadata" />
		<v-card class="block-editor-widget-layout-new width-100" :class="{ 'wide-widget-max-width': isWideWidget }">
			<div class="title-container" v-if="titleEnabled" @click.stop="focusOnTitle" :class="{ selected: isSideBarFocusOnTitle }">
				<div class="title_part text-left" :class="{ selected: isSideBarFocusOnTitle, tile: isTileEditor }">
					<EditableTextSpan
						:editOnClick="false"
						:value.sync="titleValue"
						:enableOnlyOnEditMode="true"
						placeholder="Interface title"
						class="widget-title-override pointer-events-none"
					/>
					<DropdownRotatingArrow v-if="isTileEditor" class="arrow" rotate />
				</div>
			</div>
			<div>
				<div class="first_field mt-5" v-show="localFields.length < 1 && hasEditPermissions">
					<v-menu
						v-model="firstFieldMenu"
						origin="center bottom"
						transition="slide-y-transition"
						content-class="widget-selector-modal-menu"
						:nudge-bottom="35"
						:min-width="100"
						:close-on-content-click="false"
					>
						<template v-slot:activator="{ on, attrs, value }">
							<div
								v-on="on"
								v-bind="attrs"
								@click="clearSidebarFocus"
								:class="{ selected: value }"
								class="add_field_btn flex-centerize flex-column"
							>
								<SIcon class="ic" :name="'add-field-new'" />
								<div>Click here to add a block</div>
							</div>
						</template>
						<WidgetSelectorModal
							:isTileEditor="isTileEditor"
							:isOpen="menu"
							:selectedFieldType.sync="selectedFieldType"
							:showConfigurationOnlyFields="showConfigurationOnlyFields"
							@addNewField="addNewField"
						/>
					</v-menu>
				</div>
				<div class="fields_part overflow-y-auto py-2" v-if="localFields.length >= 1" ref="fields_part">
					<Container
						group-name="1"
						@drop="onDrop($event)"
						v-if="localFields.length > 0"
						:drop-placeholder="dropPlaceholderOptions"
						:get-child-payload="getChildPayload"
						:tag="{ value: 'div', props: { class: 'row no-gutters' } }"
						non-drag-area-selector=".non-drag-area-class"
					>
						<BlockEditorWidgetLayoutDraggable
							:field="field"
							:key="field.id"
							v-on="$listeners"
							@showMenu="showMenu"
							:testResponse="testResponse"
							v-for="field in localFields"
							:previousSteps="previousSteps"
							:replaceInsertDataStringDict="replaceInsertDataStringDict"
							:configurationsMappingBlocksUUIDs="configurationsMappingBlocksUUIDs"
							:relativeToField="relativeToField"
						/>
					</Container>

					<div
						v-if="!isMiniApp && hasEditPermissions"
						ref="add_section_widget"
						class="add_section_widget flex-centerize"
						:class="{ 'selected-solid-border': menu }"
					>
						<div @click="showMenu(localFields[localFields.length - 1], $event, 'bc', $refs.add_section_widget.getBoundingClientRect())">
							<v-btn height="30" width="30" style="background: #f5f8fe">
								<SIcon name="add-field-new" class="add-field-btn-content" />
							</v-btn>
						</div>
					</div>
					<v-menu
						v-model="menu"
						:position-x="x"
						:position-y="y"
						absolute
						offset-y
						content-class="widget-selector-modal-menu"
						:close-on-content-click="false"
					>
						<WidgetSelectorModal :isTileEditor="isTileEditor" :isOpen="menu" @addNewField="addNewField" />
					</v-menu>
				</div>
				<!-- <v-menu v-model="menu" :position-x="x" :position-y="y" absolute offset-y content-class="men widget-selector-modal-menu">
					<WidgetSelectorModal @addNewField="addNewField" :showConfigurationOnlyFields="showConfigurationOnlyFields" />
				</v-menu> -->
				<!-- This created an aditional modal behind (!) the displayed modal!!! -->
			</div>

			<div v-if="!hideBtnSection" class="buttons-section mt-5" @click.stop="focusOnSubmit" :class="{ focused: sideBarFocus === 'submit' }">
				<v-divider :style="`opacity: ${sideBarFocus === 'submit' ? 0 : 1}`"></v-divider>
				<FooterField class="footer" :plainMode="isTileEditor" :field="footerField" />
			</div>
		</v-card>
	</div>
</template>

<script>
import { cloneDeep } from 'lodash';
import { Container } from 'vue-smooth-dnd';
import { mapGetters, mapActions } from 'vuex';

import BlockEditorWidgetLayoutDraggable from './BlockEditorWidgetLayoutDraggable';
import WidgetFieldService from './WidgetFieldService';
import WidgetSelectorModal from './WidgetSelectorModal/WidgetSelectorModal';
import FooterField from './WidgetFields/FooterField/FooterField';
import WorkflowService from '@/utils/WorkflowService';
import DropdownRotatingArrow from '../common/RotatingArrow/DropdownRotatingArrow.vue';
import WidgetFormLogo from '@/components/BlockEditorWidget/WidgetFormLogo';
import FIELD_CONFIGURATIONS_MAP from '@/components/BlockEditorWidget/widgetFieldTypes';
import { FieldType } from '@/components/BlockEditorWidget/widgetFieldTypes.const';
import { ArrowIcons } from '@/components/common/RotatingArrow/types';
import { isEndRunWidget } from '@/store/modules/selectedNode/getters';
import { isOfTypeTool, editWorkflow } from '@/store/modules/selectedWorkflow/getters';
import { updateWidgetFieldEdit, setWidgetTitle, setEditField } from '@/store/modules/widgetEditor/actions';
import { footerField } from '@/store/modules/widgetEditor/getters';
import { mapGettersShorthand, mapActionsShorthand } from '@/store/utils/mappers';

export default {
	name: 'BlockEditorWidgetLayout',
	props: {
		hideBtnSection: { type: Boolean, default: false },
		isMiniApp: { type: Boolean },
		testResponse: { type: Object },
		sideBarFocus: { type: String },
		previousSteps: { type: Object },
		hasEditPermissions: { type: Boolean },
		replaceInsertDataStringDict: { type: Object },
		configurationsMappingBlocksUUIDs: { type: Array },
		showConfigurationOnlyFields: { type: Boolean, default: false },
	},
	components: {
		Container,
		WidgetFormLogo,
		WidgetSelectorModal,
		BlockEditorWidgetLayoutDraggable,
		FooterField,
		DropdownRotatingArrow,
	},
	computed: {
		...mapGettersShorthand({ editWorkflow, footerField }),
		...mapGetters([isOfTypeTool.fullPath, isEndRunWidget.fullPath]),
		...mapGetters('selectedNode', ['editNode']),
		...mapGetters('widgetEditor', ['widgetFields', 'editField', 'widgetMetadata', 'widgetTitle']),
		localFields() {
			return WidgetFieldService.getFormFields(this.widgetFields);
		},
		isTileEditor() {
			return WorkflowService.isOfTypeTile(this.editWorkflow);
		},
		isTool() {
			return this[isOfTypeTool.fullPath];
		},
		isEndRunWidget() {
			return this[isEndRunWidget.fullPath];
		},
		widgetLogo() {
			return this.widgetMetadata.style?.logo || {};
		},
		widgetLogoSrc() {
			return this.widgetLogo.value;
		},
		isSideBarFocusOnTitle() {
			return this.sideBarFocus === 'title';
		},
		isWideWidget() {
			return this.widgetOptions.is_wide?.active;
		},
		widgetOptions() {
			return this.widgetMetadata?.options || {};
		},
		titleEnabled() {
			if (!('title' in this.widgetOptions)) {
				return false;
			}
			return this.widgetOptions.title.active;
		},
		titleValue: {
			set(value) {
				const oldTitleOption = cloneDeep(this.widgetOptions.title || {});
				if (
					!this.editNode.node_title ||
					this.widgetTitle === this.editNode.node_title ||
					oldTitleOption.value === this.editNode.node_title
				) {
					this.setParamInEditNode({ key: 'node_title', value });
				}

				oldTitleOption.value = value;
				this[setWidgetTitle.fullPath](value);
				this.setWidgetOption({ key: 'title', value: oldTitleOption });
			},
			get() {
				return this.isTileEditor ? this.editWorkflow.workflow_title : this.widgetOptions.title.value || '';
			},
		},
		filledIcon() {
			return ArrowIcons.Filled;
		},
	},
	watch: {
		selectedFieldType(n, o) {
			if (n !== o && n) {
				this.updateFieldType(this.selectedFieldType);
			}
		},
		menu(newVal) {
			if (!newVal) {
				this.relativeToField = null;
			}
		},
	},
	methods: {
		...mapActions([setWidgetTitle.fullPath]),
		...mapActions('selectedNode', ['setParamInEditNode']),
		...mapActions('widgetEditor', ['addField', 'updateFieldsOrder', 'setWidgetOption']),
		...mapActionsShorthand({ setEditField, updateWidgetFieldEdit }),

		async focusOnSubmit() {
			if (!this.isMiniApp) {
				this.$emit('clear-sidebar-focus');
				if (!this.footerField) {
					// this is for backwards compatibility 1.11.2023
					const newFooterField = { ...FIELD_CONFIGURATIONS_MAP[FieldType.WidgetFooter] };
					await this.addNewField(newFooterField);
					if (this.widgetOptions.submit_text?.value) {
						this.footerField.metadata.options.primary_button_value = this.widgetOptions.submit_text?.value;
					}
					this.updateWidgetFieldEdit(this.footerField);
				}
				this.setEditField(this.footerField);
			}
		},
		focusOnTitle() {
			this.$emit('focus-on-title');
		},
		clearSidebarFocus() {
			this.$emit('clear-sidebar-focus');
		},
		showMenuDropDown() {
			// This function is triggered from parent
			this.scrollToFormBottom();
			this.showMenu(this.localFields[this.localFields.length - 1], null, 'bc', this.$refs.add_section_widget.getBoundingClientRect());
		},
		scrollToFormBottom() {
			setTimeout(() => {
				const formBottom = this.$refs.fields_part;
				formBottom.scrollTop = formBottom.scrollHeight;
			}, 400);
		},
		async addNewField(field) {
			this.menu = false;
			this.firstFieldMenu = false;

			let fieldIndex = this.localFields.findIndex((f) => f.id === this.relativeToField?.id);

			if (field.type === FieldType.WidgetFooter) {
				if (this.isEndRunWidget) {
					field.defaultMetadata.options.secondary_button_enabled = false;
				}
				fieldIndex = this.localFields.length - 1;
			}
			const ind = this.addPos == 'tc' ? fieldIndex : fieldIndex + 1;
			const o = {
				payload: field,
				addedIndex: ind,
				removedIndex: null,
			};

			const ret = await this.addField(o);
			this.scrollToFormBottom();
			return ret;
		},
		showMenu(field, e, addPos, fieldRect) {
			this.addPos = addPos;
			this.relativeToField = field;
			e?.preventDefault();
			const menuWidthProperty = this.isTileEditor ? '--tile-widget-selector-modal-width' : '--widget-selector-modal-width';
			const menuWidth = window.getComputedStyle(document.documentElement).getPropertyValue(menuWidthProperty).replace('px', ''); //Strip px off
			this.x = fieldRect.left + fieldRect.width / 2 - menuWidth / 2;
			this.y = fieldRect.top;
			if (this.addPos !== 'tc') {
				this.y += fieldRect.height;
			}
			this.$nextTick(() => {
				this.menu = true;
			});
		},
		getChildPayload(index) {
			return this.localFields[index];
		},
		onDrop(dropResult) {
			const { payload, removedIndex, addedIndex } = dropResult;
			if (removedIndex === null && addedIndex === null) return;
			if (payload && payload.temp) {
				this.addField(dropResult);
			} else {
				this.updateFieldsOrder(dropResult);
			}
		},
	},
	data: () => ({
		showAddBtns: true,
		fieldIndex: null,
		menu: false,
		firstFieldMenu: false,
		x: 0,
		y: 0,
		addPos: null,
		fieldMenu: {},
		testMode: false,
		selectedFieldType: '',
		dropPlaceholderOptions: {
			className: 'drop-preview',
			showOnTop: true,
		},
		relativeToField: null,
	}),
};
</script>

<style lang="scss">
.widget-selector-modal-menu {
	background-color: var(--bg-color);
	box-shadow: var(--secondary-box-shadow);
	border: 1px solid #e4e8f2;
	border-radius: 8px;
}
</style>

<style scoped lang="scss">
$side-padding: 20px;
$arrow-height: 12px; // same as the height of the arrow in the title

.add_section_widget {
	// It has to be underscore because of the ref
	height: 35px;
	margin: 15px 19px;
	border-radius: 6px;
	background: #f5f8fe;

	&:hover {
		border: 1px dashed var(--primary-color);
	}
}

.selected-solid-border {
	border: 1px solid var(--primary-color);
}

.widget-title-override {
	::v-deep {
		.v-input input {
			// margin-left: -30px;
			font-size: 25px !important;
			color: var(--primary-font-color);
			font-family: Poppins;
			font-weight: 500;
		}
	}
}

.block-editor-widget-layout-new {
	max-height: 100%;
	position: relative;
	display: inline-block;
	background: var(--bg-color);
	border-radius: 10px !important;
	box-shadow: var(--card-shadow);

	h2 {
		color: #292829;
		font-size: 22px;
		font-weight: 500;
	}
}
.title_part {
	position: relative;
	min-height: 50px;
	margin: 0 $side-padding;

	::v-deep .v-text-field__slot {
		color: #292829;
		font-size: 22px;
		font-weight: 500;
		padding: 0 $side-padding;
	}
	&.tile {
		margin: 0;
		::v-deep .v-text-field__slot {
			padding: 0;
		}
	}
	.arrow {
		position: absolute;
		top: calc(50% - #{$arrow-height} / 2);
		right: 0;
	}
}
.title-container {
	position: relative;
	margin-bottom: -15px;
	padding: 20px 10px 10px 10px;
	background: var(--bg-color);
	border: 1px dashed transparent;
	z-index: 99;

	&:hover {
		border-color: var(--primary-color);
		background: var(--button-background);

		.title_part {
			border: none;
			::v-deep .v-input__slot {
				background: var(--button-background) !important;
			}
		}
	}
	&.selected {
		border: 1px solid var(--primary-color);
	}
}

.first_field {
	height: 70px;
	background: #f5f8fe;
	border-radius: 6px;
	box-shadow: none !important;
	position: relative;
	margin: 0px $side-padding;

	.v-menu {
		display: block;
	}
}

.buttons-section {
	border: 1px dashed transparent;
	border-radius: 0px 0px 8px 8px !important;
	padding: 0px $side-padding 14px $side-padding !important;
	&:hover {
		cursor: pointer;
		border-color: var(--primary-color);
		background: var(--button-background);
	}
	&.focused {
		border: 1px solid var(--primary-color);
	}
}
.add_field_btn {
	position: absolute;
	top: 50%;
	left: 50%;
	opacity: 1;
	margin-top: -1px;
	z-index: 2;
	visibility: visible;
	transform: translate(-50%, -50%);
	color: var(--primary-color);
	font-size: 14px;
	width: 100%;
	height: 100%;
	border-radius: 6px;
	&:hover {
		border: 1px dashed var(--primary-color);
	}
	&.selected {
		border: 1px solid var(--primary-color);
	}
}

.fields_part {
	position: relative;
	$fields-part-max-height: 50vh;
	max-height: $fields-part-max-height;
}
.add-field-btn-content {
	cursor: pointer;
	width: 19px;
	height: 19px;
	border-radius: 50%;
	box-shadow: 0px 2px 3px rgba(0, 0, 0, 0.2);
}
.striped {
	background: repeating-linear-gradient(45deg, var(--bold-border-color), var(--bold-border-color) 3px, grey 3px, grey 6px);
}
.powered-by {
	font-size: 10px;
	color: var(--secondary-font-color);
}
</style>
